import { defineComponent, nextTick, type PropType } from 'vue';

import { Gender } from '@/constants/Gender';
import { PersonType } from '@/constants/PersonType';
import { isForeignNationality as isForeignNationalityValue, nationalityOptions } from '@/constants/nationalityOptions';
import type { ClientRequest } from '@/features/Clients/Client.dto';
import type { ICase } from '@/interfaces';
import ClientFormSchema from '@/schemas/RenterFormSchema';
import { PublicService } from '@/services/publicService';
import { NAlert, NForm, NFormItemGi, NGrid, NInput, NRadio, NRadioGroup, NSelect } from 'naive-ui';
import {
	AppInputCpf,
	AppInputDate,
	AppInputMoney,
	AppInputDocument,
	AppInputCnpj,
} from '../Inputs';
import ContactForm from './ContactForm';
import { defineForm } from './FormBase';

export interface IRenterFormAuto {
	validate(): Promise<unknown>;
}

export const RenterFormAuto = defineComponent({
	name: 'RenterFormAuto',
	props: {
		showLegalPerson: {
			type: Boolean,
			default: true,
		},

		case: {
			type: Object as PropType<ICase>,
			default: null,
			required: false,
		},
	},

	extends: defineForm<ClientRequest>(),

	data() {
		return {
			documentType: null as 'cpf' | 'cnpj' | null,
			isDocumentComplete: false,
			initialEmail: undefined as string | undefined,
			initialPhone: undefined as string | undefined,
		};
	},

	computed: {
		blockDuplicates() {
			const _documentList = {
				documents: new Array<string>(),
				emails: new Array<string>(),
				phones: new Array<string>(),
			};

			if (this.case) {
				const renters = [
					this.case.quotation.renterA,
					this.case.quotation.renterB,
					this.case.quotation.renterC,
					this.case.quotation.renterD,
				];

				renters.forEach((r) => {
					// Skip the current renter's own data by reference equality
					if (!r || r === this.formData) return;

					if (r.document) _documentList.documents.push(r.document);
					if (r.contact?.email) _documentList.emails.push(r.contact.email);
					if (r.contact?.phone) _documentList.phones.push(r.contact.phone);
				});
			}

			return _documentList;
		},

		requestingLegalPerson() {
			return this.formData.personType == PersonType.LegalPerson;
		},

		detectedPersonType() {
			// Corresponsável (showLegalPerson=false) nunca é PJ: força PF assim que o documento
			// estiver completo, mesmo que seja um CNPJ (14 dígitos).
			if (!this.showLegalPerson) {
				return this.isDocumentComplete ? PersonType.Natural : null;
			}
			if (this.documentType === 'cpf') {
				return PersonType.Natural;
			} else if (this.documentType === 'cnpj') {
				return PersonType.LegalPerson;
			}
			return null;
		},

		isBusinessPerson() {
			const jobs = ['Empresário'];
			return jobs.includes(this.formData.occupation);
		},

		isForeignNationality() {
			return isForeignNationalityValue(this.formData.nationality);
		},
	},

	watch: {
		formData: {
			immediate: true,
			deep: true,
			handler(newValue) {
				if (newValue.document) {
					const digits = newValue.document.replace(/\D/g, '');
					if (digits.length === 11 && this.documentType !== 'cpf') {
						this.documentType = 'cpf';
						this.isDocumentComplete = true;
						this.formData.personType = PersonType.Natural;
					} else if (digits.length === 14 && this.documentType !== 'cnpj') {
						this.documentType = 'cnpj';
						this.isDocumentComplete = true;
						// Corresponsável nunca é PJ, mesmo digitando um CNPJ.
						this.formData.personType = this.showLegalPerson
							? PersonType.LegalPerson
							: PersonType.Natural;
					} else if (digits.length < 11) {
						this.documentType = null;
						this.isDocumentComplete = false;
					}
				}
			},
		},
	},

	created() {
		// Capture initial contact values from model (important when component is re-mounted, e.g. navigating back)
		const contact = (this as any).formData?.contact;
		if (contact) {
			this.initialEmail = contact.email;
			this.initialPhone = contact.phone;
		}
	},

	methods: {
		handleDocumentTypeDetected(type: 'cpf' | 'cnpj') {
			this.documentType = type;
			this.isDocumentComplete = true;

			// Define o personType automaticamente. Corresponsável (showLegalPerson=false) nunca é PJ.
			if (type === 'cpf') {
				this.formData.personType = PersonType.Natural;
			} else if (type === 'cnpj') {
				this.formData.personType = this.showLegalPerson
					? PersonType.LegalPerson
					: PersonType.Natural;
			}

			this.$emit('update:modelValue', this.formData);
		},

		async searchRenter() {
			// Não buscar/autofillar um documento que já está na proposta: a validação de duplicado
			// (NoDuplicatesNormalized) já orienta o usuário, e assim evitamos puxar/sobrescrever dados.
			const digits = (this.formData.document || '').replace(/\D/g, '');
			if (!digits || this.blockDuplicates.documents.some((d) => (d || '').replace(/\D/g, '') === digits))
				return;

			// Busca pela identidade (Document) apenas. Não enviar o documento como cnpj: um
			// corresponsável Empresário (PF) compartilha o CNPJ da empresa do RenterA, e casar por
			// esse CNPJ traria/sobrescreveria o registro do RenterA (PJ).
			const response = await PublicService.getRenter(this.formData.document);
			const data = response.data as any;
			if (!data) return;
			nextTick(() => {
				if (!this.showLegalPerson && data.personType === PersonType.LegalPerson) {
					// Corresponsável nunca pode ser PJ: converte para PF Empresário. A identidade da
					// pessoa é o seu CPF (Document do PJ) e o CNPJ vira associação; mantém o name (num
					// PJ é o nome do responsável) e descarta a razão social (fantasyName) e o id vazado
					// para não reusar/sobrescrever o registro PJ.
					this.formData = {
						...data,
						id: undefined,
						personType: PersonType.Natural,
						occupation: 'Empresário',
						fantasyName: '',
						document: data.document,
						cnpj: data.cnpj,
					};
					this.documentType = 'cpf';
				} else {
					// PJ mostra o CNPJ no campo de documento (backend guarda Document=CPF, Cnpj=CNPJ).
					// Só é PJ de fato com CNPJ preenchido: PersonType 0 é o default de linha gravada sem o
					// campo (RenterService), e um CPF nesse estado trocaria o documento por null — o campo
					// sumia ao sair dele (achado de 21/10; 423 cadastros reais na cópia de produção).
					const isCompany =
						data.personType === PersonType.LegalPerson && !!(data.cnpj || '').replace(/\D/g, '');
					this.formData = {
						...data,
						document: isCompany ? data.cnpj : data.document,
						cnpj: isCompany ? data.document : data.cnpj,
					};
					const digits = (this.formData.document || '').replace(/\D/g, '');
					if (digits.length === 11) {
						this.documentType = 'cpf';
						this.formData.personType = PersonType.Natural;
					} else if (digits.length === 14) {
						this.documentType = 'cnpj';
						this.formData.personType = PersonType.LegalPerson;
					}
				}
				this.isDocumentComplete = true;
				this.initialEmail = this.formData.contact?.email;
				this.initialPhone = this.formData.contact?.phone;
				this.$emit('update:modelValue', this.formData);
			});
		},
	},

	render() {
		// Só mostra os campos se o documento estiver completo e identificado
		const showFields = this.isDocumentComplete && this.documentType !== null;
		const isNaturalPerson = this.detectedPersonType === PersonType.Natural;
		const isLegalPerson = this.detectedPersonType === PersonType.LegalPerson;
		const isBusinessPerson = this.isBusinessPerson;

		return (
			<NForm
				size="large"
				ref="formRef"
				{...{ attrs: this.$attrs }}
				label-placement="top"
				rules={ClientFormSchema(this.formData, this.blockDuplicates)}
				model={this.formData}
				onSubmit={this.handleSubmit}
			>
				<div class="mt-2">
					<NGrid cols="1 400:2" x-gap="4" y-gap="4">
						<NFormItemGi
							label={
								!this.showLegalPerson
									? 'CPF'
									: this.documentType === 'cpf'
										? 'CPF'
										: this.documentType === 'cnpj'
											? 'CNPJ'
											: 'CPF ou CNPJ'
							}
							path="document"
						>
							{this.showLegalPerson ? (
								<AppInputDocument
									data-cy="input-document"
									onChange={this.searchRenter}
									onDocumentTypeDetected={this.handleDocumentTypeDetected}
									v-model={this.formData.document}
								/>
							) : (
								// Corresponsável só pode ser PF: usa o input de CPF (11 dígitos),
								// que impede fisicamente digitar um CNPJ.
								<AppInputCpf
									data-cy="input-document"
									onChange={this.searchRenter}
									v-model={this.formData.document}
								/>
							)}
						</NFormItemGi>

						{/* Nome e Data de nascimento existiam no schema (IsRequired, MinimalNameLength,
						    IsOver18YearsOld) mas não tinham input renderizado — e o Naive UI só valida
						    path com form item montado. Resultado: locatário que o enriquecimento não
						    achava era criado com Name vazio e BirthDate nulo, que depois derrubava a
						    emissão da apólice e o portal do locatário. Renderizar aqui é o que faz as
						    regras já declaradas passarem a valer. */}
						{showFields && (
							<NFormItemGi
								label={isLegalPerson ? 'Nome do responsável' : 'Nome completo'}
								path="name"
							>
								<NInput
									data-cy="input-name"
									v-model:value={this.formData.name}
									placeholder={isLegalPerson ? 'Nome do responsável legal' : 'Nome completo'}
								/>
							</NFormItemGi>
						)}

						{showFields && (
							<NFormItemGi label="País de origem" path="nationality">
									<NSelect
										data-cy="input-nationality"
										v-model:value={this.formData.nationality}
										options={nationalityOptions}
										placeholder="Selecione o país de origem"
									filterable
								/>
							</NFormItemGi>
						)}

						{showFields &&
							isNaturalPerson && [
								// Só em PF: a proposta da Porto manda dataNascimento apenas para pessoa
								// física, e PJ usa a data de abertura.
								<NFormItemGi label="Data de nascimento" path="birthDate">
									<AppInputDate data-cy="input-birthDate" v-model={this.formData.birthDate} />
								</NFormItemGi>,

								<NFormItemGi label="Renda declarada" path="declaredIncome">
									<AppInputMoney
										data-cy="input-declaredIncome"
										v-model={this.formData.declaredIncome}
									/>
								</NFormItemGi>,

								<NFormItemGi label="Ocupação" path="occupation">
									<NSelect
										v-model:value={this.formData.occupation}
										options={[
											{ label: 'Assalariado', value: 'Assalariado' },
											{
												label: 'Autonomo/Profissional liberal',
												value: 'Autonomo/Profissional liberal',
											},
											{ label: 'Empresário', value: 'Empresário' },
											{ label: 'Aposentado', value: 'Aposentado' },
										]}
									/>
								</NFormItemGi>,

								<NFormItemGi label="Gênero" path="gender">
									<NRadioGroup v-model:value={this.formData.gender}>
										<NRadio data-cy="input-gender-male" label="Masculino" value={Gender.MALE} />
										<NRadio data-cy="input-gender-female" label="Feminino" value={Gender.FEMALE} />
									</NRadioGroup>
								</NFormItemGi>,

								isBusinessPerson && (
									<NFormItemGi label={'CNPJ'} path="cnpj">
										<AppInputCnpj data-cy="input-document" v-model={this.formData.cnpj} />
									</NFormItemGi>
								),
							]}

						{showFields &&
							isLegalPerson && [
								<NFormItemGi label="Razão Social" path="fantasyName">
									<NInput
										data-cy="input-fantasyName"
										v-model:value={this.formData.fantasyName}
										placeholder="Razão Social"
									/>
								</NFormItemGi>,

								<NFormItemGi label="Data de abertura" path="openingDate">
									<AppInputDate data-cy="input-openingDate" v-model={this.formData.openingDate} />
								</NFormItemGi>,

								<NFormItemGi label="Renda declarada" path="declaredIncome">
									<AppInputMoney
										data-cy="input-declaredIncome"
										v-model={this.formData.declaredIncome}
									/>
								</NFormItemGi>,
							]}
					</NGrid>

					{showFields && isLegalPerson && (
						<div class="mt-2">
							<NGrid cols="1 400:2" x-gap="4" y-gap="4">
								<NFormItemGi label="CPF do responsável" path="cnpj">
									<AppInputCpf data-cy="input-responsible-cpf" v-model={this.formData.cnpj} />
								</NFormItemGi>

								<NFormItemGi label="Gênero do responsável" path="gender">
									<NRadioGroup v-model:value={this.formData.gender}>
										<NRadio data-cy="input-gender-male" label="Masculino" value={Gender.MALE} />
										<NRadio data-cy="input-gender-female" label="Feminino" value={Gender.FEMALE} />
									</NRadioGroup>
								</NFormItemGi>
							</NGrid>
						</div>
					)}

					{showFields && (
						<ContactForm
							ref="formContactRef"
							v-model={this.formData.contact}
							renterId={(this.formData as any).id}
							originalEmail={this.initialEmail}
							originalPhone={this.initialPhone}
							forbiddenEmails={this.blockDuplicates.emails}
							forbiddenPhones={this.blockDuplicates.phones}
						/>
					)}

					{showFields && this.isForeignNationality && (
						<NAlert type="warning" class="mt-2" title="Cliente estrangeiro">
							Esta proposta será direcionada para análise manual. Serão solicitados Imposto de Renda,
							extratos bancários ou holerites dos últimos 3 meses, e a inclusão de segundo locatário
							(corresponsável).
						</NAlert>
					)}
				</div>
			</NForm>
		);
	},
});

export default RenterFormAuto;
